import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { NavigationStart, Router } from '@angular/router'
import { MatDialog } from '@angular/material/dialog'
import { Subject } from 'rxjs'
import { filter, startWith, takeUntil } from 'rxjs/operators'
import * as moment from 'moment-timezone'
import { CommonService } from '../../services/common/common.service'
import { LoaderService } from '../../services/loader/loader.service'
import { SidenavService } from '../../services/sidenav.service'
import { UtilService } from '../../services/util/util.service'
import { ICONS, ICON_PATHS } from '../../constants/icons'
import { WebsocketService } from '../../services/websocket.service'
import { TenantDialogComponent } from '../tenant-dialog/tenant-dialog.component'
import { MenuItem, Notification, UserInfo } from './c-header.interface'

@Component({
  selector: 'app-c-header',
  templateUrl: './c-header.component.html',
  styleUrls: ['./c-header.component.scss'],
  providers: [WebsocketService],
  standalone: false,
})
export class CHeaderComponent implements OnInit, AfterViewChecked, OnDestroy {
  private readonly destroy$ = new Subject<void>()
  private readonly USER_TYPE_APPROVER = 4
  readonly ICONS = ICONS
  readonly ICON_PATHS = ICON_PATHS

  SelectedTab: number = 0
  SelectedReportTab: number = 0
  SelectedConnectorTab: number = 0
  private wsConnected = false
  userInfo: UserInfo = {} as UserInfo
  userList: any[] = []
  keys: string[] = []
  notificationCount: number | null = null
  locationName = ''
  notification: any
  helpModal = false
  profilePicture = ''
  showManageMenu = false
  showReportMenu = false
  showConnectorMenu = false
  showDashboardMenu = false
  showHead = false

  listOfTabs: MenuItem[] = [
    {
      tab: 'All Documents',
      routePath: 'all-documents',
    },
    {
      tab: 'AR Documents',
      routePath: 'ar-documents',
    },
    {
      tab: 'Promotions',
      routePath: 'promotions',
    },
    {
      tab: 'New Remittance',
      routePath: 'new-remittance',
    },
    {
      tab: 'Validations',
      routePath: 'validation',
    },
    {
      tab: 'Awaiting Approval',
      routePath: 'approvals',
    },
    {
      tab: 'Approved Deductions',
      routePath: 'approved-deduction',
    },
    {
      tab: 'Track and Manage Disputes',
      routePath: 'track-dispute',
    },
  ]

  reportMenu: MenuItem[] = [
    {
      tab: 'Remittance Summary',
      routePath: 'remittance-summary',
    },
    {
      tab: 'Invoice Summary',
      routePath: 'invoice-summary',
    },
    {
      tab: 'Open Debit Memos',
      routePath: 'debit-memos',
    },
    {
      tab: 'Deduction by Customers',
      routePath: 'deduction-by-customers',
    },
    {
      tab: 'Credit Report',
      routePath: 'generate-credit-report',
    },
    {
      tab: 'My Downloads',
      routePath: 'file-download',
    },
  ]

  readonly connectorMenu: MenuItem[] = [
    {
      tab: 'Connectors',
      routePath: 'retailer-connector',
    },
  ]

  constructor(
    private readonly router: Router,
    private readonly commonserv: CommonService,
    public readonly loaderService: LoaderService,
    public readonly sidenavService: SidenavService,
    private readonly cdr: ChangeDetectorRef,
    private readonly WebsocketService: WebsocketService,
    public readonly utilService: UtilService,
    private readonly dialog: MatDialog
  ) {
    this.getUserInfo()
    this.loaderService.userList
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.userList = data
      })
  }

  ngOnInit(): void {
    // Setup router events listener once
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        startWith(this.router),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        let url = window.location.pathname
        if (event instanceof NavigationStart) {
          url = event.url
        }
        if (!url.includes('login')) {
          this.handleNavigation(url)
        }
      })

    this.loaderService.userInfo
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        if (data && Object.keys(data).length !== 0) {
          this.userInfo = data
          this.showHead = true

          // Set profile picture from API response
          if (
            data.logo &&
            typeof data.logo === 'string' &&
            data.logo.trim() !== ''
          ) {
            this.profilePicture = data.logo
          } else {
            // Fallback to default logo if no profile picture
            this.profilePicture = ICON_PATHS.DMP_LOGO
          }

          // Configure tabs based on user type and permissions
          if (data.user_type === this.USER_TYPE_APPROVER) {
            this.listOfTabs = [
              {
                tab: 'Awaiting Approval',
                routePath: 'approvals',
              },
            ] as MenuItem[]
          } else {
            // Remove tabs based on user permissions
            if (data.ar_documents === false) {
              this.listOfTabs = this.listOfTabs.filter(
                (tab) => tab.routePath !== 'ar-documents'
              )
            }
            if (data.trade_promotions === false) {
              this.listOfTabs = this.listOfTabs.filter(
                (tab) => tab.routePath !== 'promotions'
              )
            }
            if (data.credit_report === false) {
              this.reportMenu = this.reportMenu.filter(
                (tab) => tab.routePath !== 'generate-credit-report'
              )
            }
          }

          // Initialize WebSocket after tenant_id is guaranteed to be in sessionStorage.
          // On first login tenant_id is set just before userInfo emits, so connecting here
          // ensures the WebSocket URL is built with valid credentials, fixing the missing
          // notification count on initial login.
          if (!this.wsConnected) {
            this.wsConnected = true
            this.WebsocketService.reconnect()
            this.connectWebSocket()
          }

          // Trigger change detection to ensure UI updates
          this.cdr.detectChanges()
        }
      })
  }

  handleNavigation(url: string): void {
    this.showDashboardMenu = false
    this.locationName = url
    if (url === '/login') {
      this.showHead = false
    } else {
      this.showHead = true
      if (url === '/dashboard') {
        this.showManageMenu = false
        this.showDashboardMenu = true
      } else {
        // For manage deductions,set SelectedTab based on URL
        if (url.startsWith('/manage-deductions/')) {
          this.showManageMenu = true
          this.showReportMenu = false
          this.showConnectorMenu = false
          const index = this.listOfTabs.findIndex(
            (tab) => url.indexOf(tab.routePath) !== -1
          )
          if (index !== -1) {
            this.SelectedTab = index
          }
        } else if (url.startsWith('/report/')) {
          this.showManageMenu = false
          this.showReportMenu = true
          this.showConnectorMenu = false
          const index = this.reportMenu.findIndex(
            (tab) => url.indexOf(tab.routePath) !== -1
          )
          if (index !== -1) {
            this.SelectedReportTab = index
          }
        } else if (url.startsWith('/connector/')) {
          this.showManageMenu = false
          this.showReportMenu = false
          this.showConnectorMenu = true
          const index = this.connectorMenu.findIndex(
            (tab) => url.indexOf(tab.routePath) !== -1
          )
          if (index !== -1) {
            this.SelectedConnectorTab = index
          }
        }
      }
    }
  }

  ngAfterViewChecked(): void {
    this.locationName = window.location.pathname
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  Helpmodal(event) {
    this.helpModal = true
    event.stopPropagation()
  }
  @HostListener('document:click', ['$event'])
  clickout(): void {
    this.helpModal = false
  }

  selectMenu(event: any): void {
    const tab = this.listOfTabs[event.index]

    if (this.userInfo.user_type === this.USER_TYPE_APPROVER) {
      if (event.index === 0) {
        this.router.navigate(['/manage-deductions/approvals'])
      }
    } else {
      if (tab?.routePath) {
        this.router.navigate([`/manage-deductions/${tab.routePath}`])
      }
    }
  }

  selectReportMenu(event: any): void {
    const tab = this.reportMenu[event.index]
    switch (tab?.routePath) {
      case 'remittance-summary':
        this.router.navigate(['/report/remittance-summary'])
        break
      case 'invoice-summary':
        this.router.navigate(['/report/invoice-summary'])
        break
      case 'debit-memos':
        this.router.navigate(['/report/debit-memos'])
        break
      case 'deduction-by-customers':
        this.router.navigate(['/report/deduction-by-customers'])
        break
      case 'generate-credit-report':
        this.router.navigate(['/report/generate-credit-report'])
        break
      case 'file-download':
        this.router.navigate(['/report/file-download'])
        break
      default:
        break
    }
  }

  selectConnectorMenu(event: any): void {
    if (event.index === 0) {
      this.router.navigate(['/connector/retailer-connectors'])
    }
  }

  triggerManageDeductions(): void {
    this.showManageMenu = true
    this.showReportMenu = false
    this.showConnectorMenu = false
    if (this.userInfo.user_type === this.USER_TYPE_APPROVER) {
      this.SelectedTab = 0
      this.router.navigate(['/manage-deductions/approvals'])
    } else {
      this.router.navigate(['/manage-deductions/new-remittance'])
    }
  }

  triggerReport(): void {
    this.showReportMenu = true
    this.showManageMenu = false
    this.showConnectorMenu = false
    this.SelectedReportTab = 0
    this.router.navigate(['/report/remittance-summary'])
  }

  triggerConnector(): void {
    this.showConnectorMenu = true
    this.showReportMenu = false
    this.showManageMenu = false
    this.SelectedConnectorTab = 0
    this.router.navigate(['/connector/retailer-connectors'])
  }

  triggerDashboard(): void {
    this.showManageMenu = false
    this.showReportMenu = false
    this.showConnectorMenu = false
    this.router.navigate(['/dashboard'])
  }

  bindUserImage(image: string | null): string {
    if (image && image !== null) {
      return image
    } else {
      return ICONS.USER_CIRCLE
    }
  }

  getUserInfo(): void {
    this.loaderService.userInfo
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.userInfo = data
      })
  }

  connectWebSocket() {
    this.WebsocketService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg: any) => {
        if (msg) {
          const msgparse = JSON.parse(msg)
          const count = msgparse.result
          this.notificationCount = count > 0 ? count : null
          this.cdr.detectChanges()
        }
      })
  }

  Logout(): void {
    sessionStorage.removeItem('api_token')
    window.location.href = JSON.parse(localStorage.getItem('baseUrl')).sso.url
  }

  getNotification(): void {
    this.commonserv
      .getInvoiceMessages()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.objectKeys(res.datas)
        if (res.result === 0) {
          this.notification = []
          this.keys = []
        } else {
          this.notification = res.datas
        }
      })
  }

  objectKeys(obj: any): void {
    const keys: string[] = []
    if (!obj) {
      this.keys = keys
      return
    }
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        keys.push(key)
      }
    }
    this.keys = keys
  }

  clearAllNotification(
    notificationid: string | number,
    clearFlag: string
  ): void {
    this.commonserv
      .clearNotification(notificationid)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (clearFlag === 'clearAll') {
          this.getNotification()
        }
        if (res.result === 1 && notificationid) {
          for (const item of this.keys) {
            for (
              let i = this.notification[item].length - 1,
                msg = this.notification[item];
              i >= 0;
              --i
            ) {
              if (msg[i].notification_id == notificationid) {
                this.notification[item].splice(i, 1)
              }
            }
            if (this.notification[item].length === 0) {
              this.getNotification()
            }
          }
        }
        if (this.keys.length === 0) {
          this.notification = []
          this.keys = []
        }
        this.cdr.detectChanges()
      })
  }

  openActivitySideNavBar(notify: Notification): void {
    if (!Number(notify.details.id)) return
    if (notify.details.level === 'jobs') {
      this.redirctToRouter('/report/file-download', notify.details)
      this.sidenavService.close()
      return
    }
    this.commonserv
      .getInvoiceProcessingStatus(notify.details.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        const routerType = res.data[0].invoices_values[0].column_value
        const invoiceData = notify.details.id
        if (routerType === 'Open') {
          window.open(`${'/manage-deductions/validation'}?id=${invoiceData}`)
        } else if (
          routerType === 'Awaiting Approval' ||
          routerType === 'Awaiting Approval-Level 1' ||
          routerType === 'Awaiting Approval-Level 2' ||
          routerType === 'Awaiting Approval-Level 3'
        ) {
          window.open(`${'/manage-deductions/approvals'}?id=${invoiceData}`)
        } else if (routerType === 'Dispute')
          window.open(`${'/manage-deductions/track-dispute'}?id=${invoiceData}`)
        else if (routerType === 'Approved' || routerType === 'Threshold Credit')
          window.open(
            `${'/manage-deductions/approved-deduction'}?id=${invoiceData}`
          )
        else {
          window.open(`${'/report/invoice-summary'}?id=${invoiceData}`)
        }
        const notifyRouterData = {
          routerType: routerType,
          invoiceData: notify.details,
        }
        localStorage.setItem(
          'notificationDetails',
          JSON.stringify(notifyRouterData)
        )
      })
    this.sidenavService.close()
  }

  redirctToRouter(url: string, data: any): void {
    const routerData = { state: { openActivityInvoice: data } }
    if (this.router.url === url)
      this.loaderService.openActivityInvoice.next(data)
    else this.router.navigate([url], routerData)
  }

  shortMsg(msg: string): string {
    return msg.substring(0, 80)
  }

  getNameByID(id: number): string {
    const item = this.userList.find((item) => item.Id === id)
    return item ? `${item.FirstName} ${item.LastName}` : 'Unknown'
  }

  getEST(date: string): string {
    const cutoffString = date // in utc
    const utcCutoff = moment.utc(cutoffString, 'YYYYMMDD HH:mm:ss')
    const displayCutoff = utcCutoff.clone().tz('America/New_York')
    return displayCutoff.toString()
  }

  showTenantInfo(): void {
    this.dialog.open(TenantDialogComponent, {
      width: '450px',
      height: 'auto',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: true,
      hasBackdrop: true,
      panelClass: 'tenant-info-dialog',
      autoFocus: false,
      data: {
        currentUser: {
          tenantName: this.userInfo?.tenant_name || 'N/A',
        },
      },
    })
  }

  onProfilePicError(event: any): void {
    this.profilePicture = ICON_PATHS.DMP_LOGO
    event.target.src = this.profilePicture
  }
}
